import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import Logger from "../../common/utils/Logger";
import { fetchExtensions } from "../../api/extension";
import settings from "../../config/settings";
import NtExtensionItem from "../components/NtExtensionItem";
import routes from "../../navigation/routes";
import BaseController from "../../common/base/BaseController";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtExtensionUploadForm from "../components/NtExtensionUploadForm";
import NtModal from "../../components/NtModal";
import NtExtensionNewForm from "../components/NtExtensionNewForm";
import {
  asyncStorageSetData,
  showToast,
} from "../../common/utils/ControllerUtils";
import extensioncsvcolumndata from "../data/extensioncsvcolumndata";
import NtListHeader from "../../components/NtListHeader";
import NtExtensionAdvancedSearchPicker from "../components/NtExtensionAdvanceSearchPicker";
import { isPhysicalExtension, parseExtension } from "../utils/ExtensionUtils";
import NtListEmpty from "../../components/NtListEmpty";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtSearchBar from "../../components/NtSearchBar";

class ExtensionController extends BaseController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    totalItems: 0,
    searchText: "",
    searchFilter: null,
    page: 1,
  };

  column_location = "@extensions_column_storage";

  addAccessModalRef = React.createRef();
  uploadAccessModalRef = React.createRef();
  advanceSearchRef = React.createRef();

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      extensioncsvcolumndata.filter((each) => each.default === true),
    );

    this.handleDataFetch();
  }

  handleDataFetch = async () => {
    Logger("Fetching Extensions page", this.state.page);
    this.setState({ loading: true });

    const response = await fetchExtensions(
      this.context.user?.domain,
      this.state.page,
      settings.pageSizeLoad,
      this.state.searchText,
      this.state.searchFilter,
    );

    if (response.ok) {
      this.setState({
        data: response.data?.data || [],
        totalItems: response.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const response = await fetchExtensions(
      this.context.user?.domain,
      1,
      10000000,
      this.state.searchText,
      this.state.searchFilter,
    );
    if (response.ok) return response.data?.data;
    return [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "extension")
      return parseExtension(
        item[column],
        this.context.user?.domain,
      );
    if (column === "description") {
      if (isPhysicalExtension(item[column])) {
        return `"Gateway: ${item[column]?.split(",")[0]}, Port: ${item[column]?.split(",")[1]}"`;
      }
      return " ";
    }
    if (column === "toll_allow")
      return item[column] && item[column] === "t" ? "True" : "False";
    if (column === "effective_caller_id_name")
      return (
        item[column] ||
        parseExtension(item.extension, this.context.user?.domain)
      );
    return item[column];
  };

  handleSearch = (text) => {
    this.setState({ searchText: text, page: 1 }, () => this.handleDataFetch());
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  renderAdvanceSearchForm = () => (
    <NtModal ref={this.advanceSearchRef}>
      <NtExtensionAdvancedSearchPicker
        filter={this.state.searchFilter}
        onCancel={() => this.advanceSearchRef.current?.dismiss()}
        onFilter={(filter) => {
          this.setState({ searchFilter: filter, page: 1 }, () =>
            this.handleDataFetch(),
          );
          this.advanceSearchRef.current?.dismiss();
        }}
        onFilterClear={() => {
          this.setState({ searchFilter: null, page: 1 }, () =>
            this.handleDataFetch(),
          );
          this.advanceSearchRef.current?.dismiss();
        }}
      />
    </NtModal>
  );

  renderAddNewForm = () => (
    <NtModal ref={this.addAccessModalRef}>
      <NtExtensionNewForm
        onCancel={() => this.addAccessModalRef.current?.dismiss()}
        onCreate={() => {
          this.setState({ page: 1 }, () => this.handleDataFetch());
          this.addAccessModalRef.current?.dismiss();
          showToast("Success", "Successfully created the new extension");
        }}
      />
    </NtModal>
  );

  renderUploadForm = () => (
    <NtModal ref={this.uploadAccessModalRef}>
      <NtExtensionUploadForm
        onCancel={() => this.uploadAccessModalRef.current?.dismiss()}
        onUploaded={() => {
          this.setState({ page: 1 }, () => this.handleDataFetch());
          this.uploadAccessModalRef.current?.dismiss();
        }}
      />
    </NtModal>
  );

  renderRow = (item, index) => (
    <NtExtensionItem
      key={index}
      item={item}
      columns={this.state.columns}
      onGatewayPress={(gateway) => {
        this.props.navigation.navigate(routes.gateway, { gateway });
      }}
      onUpdate={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast(
          "Success",
          `Successfully updated extension ${parseExtension(item.extension)}`,
        );
      }}
    />
  );

  render() {
    const canEdit = this.handlePermissionCheck(settings.permissionTypeEdit);
    const count = this.state.totalItems || 0;
    const subtitle = `Configure user accounts that register SIP devices · ${count} extension${count === 1 ? "" : "s"}`;

    const actions = canEdit ? (
      <>
        <NtPageActionButton
          icon="plus"
          title="Add Extension"
          variant="primary"
          onPress={() => this.addAccessModalRef.current?.show()}
        />
        <NtPageActionButton
          icon="arrow-up"
          title="Upload CSV"
          onPress={() => this.uploadAccessModalRef.current?.show()}
        />
      </>
    ) : null;

    const searchBar = (
      <NtSearchBar
        filterCount={
          this.state.searchFilter
            ? Object.keys(this.state.searchFilter).length
            : 0
        }
        value={this.state.searchText}
        onChangeText={(text) => this.handleSearch(text)}
        onFilterPress={() => this.advanceSearchRef?.current?.show()}
      />
    );

    return (
      <NtRootPageContainer
        pageTitle="Extensions"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection
          search={searchBar}
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: canEdit ? 35 : 0 }}
              columns={extensioncsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="extension"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAddNewForm()}
        {this.renderUploadForm()}
        {this.renderAdvanceSearchForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default ExtensionController;
